import { Controller } from "@hotwired/stimulus"
import { confirmBeforeSubmit } from "../utils/form_confirmation"

Turbo.StreamActions.redirect = function () {
  Turbo.visit(this.target);
};

// Connects to data-controller="employees"
export default class extends Controller {
  static targets = [
    "activeCheckbox", "isDeletedField",
    "editModal", "editModalBody", "editForm", "editSubmitButton", "editCloseButton",
    "editActiveCheckbox", "editIsDeletedField",
    "searchForm", "searchButton", "clearButton"
  ]

  /**
   * フォーム送信前に確認ダイアログを表示します。
   * @param {Event} event - フォーム送信イベント
   * @returns {boolean|void} 確認でキャンセルされた場合はfalseを返します。
   */
  confirmBeforeSubmit(event) {
    return confirmBeforeSubmit(event, {
      submitButtonTarget: 'employees-target="editSubmitButton"'
    })
  }

  /**
   * StimulusコントローラーがDOMに接続されたときに呼び出されます。
   * イベントリスナーと初期設定を行います。
   */
  connect() {
    this._modalObservers = {}
    this.setupActiveCheckbox()
    this.setupEditActiveCheckbox()
    this._submitting = false
    this.setupBranchAuthorityControl()
    this._frameLoadListener = this.handleFrameLoad.bind(this)
    document.addEventListener('turbo:frame-load', this._frameLoadListener)

    // 社員編集モーダルのローダーを初期化
    this.modalLoader = null // 後で初期化

    // 新規モーダルの初期HTMLを保存
    this.saveNewModalInitialHtml()
  }

  /**
   * StimulusコントローラーがDOMから切断されたときに呼び出されます。
   * イベントリスナーをクリーンアップします。
   */
  disconnect() {
    if (this._frameLoadListener) {
      document.removeEventListener('turbo:frame-load', this._frameLoadListener)
    }

    if (this._branchChangeHandler) {
      document.removeEventListener('change', this._branchChangeHandler)
      this._branchChangeHandler = null
    }

    if (this._modalObservers) {
      Object.values(this._modalObservers).forEach(observer => observer.disconnect())
      this._modalObservers = {}
    }
  }

  /**
   * 所属と権限の連動制御をセットアップします。
   * 支店の選択に応じて権限ラジオボタンの状態を更新します。
   */
  setupBranchAuthorityControl() {
    // ページ全体にイベント委譲
    if (!this._branchChangeHandler) {
      this._branchChangeHandler = (event) => {
        if (event.target.matches('[data-employees-target*="branchSelect"], select[name*="branch_id"]')) {
          this.handleBranchChange(event.target)
        }
      }
      document.addEventListener('change', this._branchChangeHandler)
    }

    // モーダル監視
    this.observeModals()
  }

  /**
   * 支店選択の変更を処理します。
   * @param {HTMLSelectElement} selectElement - 変更されたselect要素
   */
  handleBranchChange(selectElement) {
    const container = selectElement.closest('#new_employee_modal, #edit_employee_modal') || document
    const authorityRadios = container.querySelectorAll('input[name*="authority"]')
    const selectedOption = selectElement.options[selectElement.selectedIndex]
    const branchCode = selectedOption?.dataset?.branchCode

    const headquartersMeta = document.querySelector('meta[name="headquarters-branch-code"]')
    const headquartersBranchCode = headquartersMeta?.content

    authorityRadios.forEach(radio => {
      const isHQ = branchCode === headquartersBranchCode
      const label = radio.closest('label')

      radio.disabled = isHQ

      if (isHQ) {
        radio.checked = radio.value === '1'
        label?.classList.add('opacity-50', 'cursor-not-allowed')
      } else {
        label?.classList.remove('opacity-50', 'cursor-not-allowed')
      }
    })
  }

  /**
   * 支店選択の変更イベントハンドラ
   * @param {Event} event - changeイベント
   */
  onBranchChange(event) {
    this.handleBranchChange(event.target)
  }  /**
   * 新規および編集モーダルの表示状態を監視し、表示されたときに初期化処理を呼び出します。
   */
  observeModals() {
    this._modalObservers ||= {}
    ['new_employee_modal', 'edit_employee_modal'].forEach(modalId => {
      const modal = document.getElementById(modalId)
      if (!modal) return

      if (this._modalObservers[modalId]) {
        this._modalObservers[modalId].disconnect()
      }

      const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.attributeName === 'open' && modal.open) {
            setTimeout(() => this.initModalControls(modal), 100)
          }
        })
      })

      observer.observe(modal, { attributes: true })
      this._modalObservers[modalId] = observer

      if (modal.open) {
        setTimeout(() => this.initModalControls(modal), 100)
      }
    })
  }

  /**
   * モーダル内のコントロール（支店選択など）を初期化します。
   * @param {HTMLElement} modal - 初期化するモーダル要素
   */
  initModalControls(modal) {
    const branchSelect = modal.querySelector('select[name*="branch_id"]')
    if (branchSelect) {
      this.handleBranchChange(branchSelect)
    }
  }

  /**
   * Turbo Frameの読み込み完了をハンドルします。
   * モーダルの再監視と初期化を行います。
   * @param {Event} event - turbo:frame-loadイベント
   */
  handleFrameLoad(event) {
    const frame = event.target
    if (!frame || !['new_employee_modal_frame', 'edit_employee_modal_frame'].includes(frame.id)) return

    this.observeModals()

    const modal = frame.querySelector('dialog')
    if (modal?.open) {
      setTimeout(() => this.initModalControls(modal), 50)
    }
  }

  /**
   * テーブル行のクリックを委譲し、編集モーダルを開きます。
   * @param {Event} event - クリックイベント
   */
  delegateOpenEdit(event) {
    const row = event.target.closest('tr[data-employee-id]')
    if (!row) return
    const id = row.dataset.employeeId || row.dataset.employeesEmployeeIdParam
    if (!id) return
    event.preventDefault()
    event.stopPropagation()
    this.openEditModal({ currentTarget: row, params: { employeeId: id } })
  }

  /**
   * 新規登録フォームの「有効」トグルチェックボックスの処理をセットアップします。
   */
  setupActiveCheckbox() {
    if (this.hasActiveCheckboxTarget && this.hasIsDeletedFieldTarget) {
      // 新規登録の場合は無効化
      if (this.activeCheckboxTarget.disabled) {
        this.isDeletedFieldTarget.value = 'false' // デフォルトで有効
        return
      }

      this.activeCheckboxTarget.addEventListener('change', (e) => {
        this.isDeletedFieldTarget.value = e.target.checked ? 'false' : 'true'
        console.log('Active toggle changed:', e.target.checked, 'is_deleted:', this.isDeletedFieldTarget.value)
      })
    }
  }

  /**
   * 編集フォームの「有効」トグルチェックボックスの処理をセットアップします。
   */
  setupEditActiveCheckbox() {
    if (this.hasEditActiveCheckboxTarget && this.hasEditIsDeletedFieldTarget) {
      this.editActiveCheckboxTarget.addEventListener('change', (e) => {
        this.editIsDeletedFieldTarget.value = e.target.checked ? 'false' : 'true'
        console.log('Edit active toggle changed:', e.target.checked, 'is_deleted:', this.editIsDeletedFieldTarget.value)
      })
    }
  }

  /**
   * 「有効」トグルの状態変更を処理し、隠しフィールドの値を更新します。
   */
  toggleActiveStatus(event) {
    const toggle = event.target
    const form = toggle.closest('form')
    if (!form) return

    const isDeletedField = form.querySelector('input[name="employee[is_deleted]"]')
    if (isDeletedField) {
      isDeletedField.value = toggle.checked ? 'false' : 'true'
      console.log('Toggle changed:', toggle.checked, 'is_deleted:', isDeletedField.value)
    }
  }

  /**
   * 社員編集モーダルを開きます。
   */
  async openEditModal(event) {
    const t = event?.currentTarget
    const employeeId = event?.params?.employeeId || t?.dataset?.employeesEmployeeIdParam || t?.dataset?.employeeId
    if (!employeeId) return

    this._lastOpenId = employeeId
    try { this._abortController?.abort() } catch (_) {}
    this._abortController = new AbortController()

    // モーダルローダーを遅延初期化（必要な時に作成）
    if (!this.modalLoader) {
      const { ModalLoaders } = await import('../utils/modal_loader')
      this.modalLoader = ModalLoaders.createEditModal('employee')
    }

    try {
      await this.modalLoader.loadWithHtml(`/employees/${employeeId}/edit`, {
        signal: this._abortController.signal
      })

      if (this._lastOpenId !== employeeId) return

      const modal = document.getElementById('edit_employee_modal')
      // 編集フォーム読み込み後に制御を初期化
      setTimeout(() => this.initModalControls(modal), 50)
    } catch (e) {
      if (e?.name !== 'AbortError') {
        console.error('Employee edit modal error:', e)
      }
    }
  }

  /**
   * 社員編集モーダルを閉じます。
   */
  closeEditModal() {
    if (this.modalLoader) {
      this.modalLoader.closeModal()
      this.modalLoader.clearModalBody()
    } else {
      // フォールバック
      const modal = document.getElementById('edit_employee_modal')
      if (modal) modal.close?.() || modal.removeAttribute('open')
      const body = document.getElementById('edit_employee_modal_body')
      if (body) body.innerHTML = ''
    }
  }

  /**
   * 新規登録モーダルを閉じてフォームをリセットします。
   * バリデーションエラー後も初期状態に戻します。
   */
  closeNewModal() {
    const modal = document.getElementById('new_employee_modal')
    if (!modal) return

    // モーダルを閉じる
    modal.close?.() || modal.removeAttribute('open')

    // アニメーション完了後にHTMLを復元
    const resetModal = () => {
      const frame = document.getElementById('new_employee_modal_frame')
      if (frame && this._newModalInitialHtml) {
        frame.innerHTML = this._newModalInitialHtml
        this.observeModals()
      } else if (modal) {
        // フォールバック: 単純なリセット
        const form = modal.querySelector('form')
        if (form) {
          form.reset()
          // エラーメッセージを削除
          form.querySelectorAll('.field_with_errors, .text-error, .alert-error').forEach(el => el.remove())
          // 支店選択後の権限制御もリセット
          const branchSelect = modal.querySelector('select[name*="branch_id"]')
          if (branchSelect) {
            this.handleBranchChange(branchSelect)
          }
        }
      }
    }

    // アニメーション完了を待ってからリセット（DaisyUIのモーダルアニメーションは約200ms）
    setTimeout(resetModal, 300)
  }

  /**
   * 新規モーダルの初期HTMLを保存します。
   * connect時またはページ読み込み時に呼び出します。
   */
  saveNewModalInitialHtml() {
    const frame = document.getElementById('new_employee_modal_frame')
    if (frame && !this._newModalInitialHtml) {
      this._newModalInitialHtml = frame.innerHTML
    }
  }



  /**
   * 検索フォームを送信します（デフォルトのフォーム送信を利用）。
   * @param {Event} event - 送信イベント
   */
  searchEmployees(event) {
    // フォームのデフォルト送信を使用
  }

  /**
   * 検索フォームをクリアし、ページをリロードします。
   * @param {Event} event - クリックイベント
   */
  clearSearch(event) {
    event.preventDefault()
    window.location.href = window.location.pathname
  }
}
